// frontend/src/utils/friendCode.js
//
// Friend-code (partner referral) primitives shared by the capture component,
// the sign-up field and the checkout block. Pure, dependency-free.
//
// PRIVACY. A friend code is a bearer value that names who earns on a
// customer: it is never logged, never put in an analytics payload and never
// left in the address bar (CaptureAcquisition strips `?ref=`). Nothing in
// this module touches a logger or analytics.

/** sessionStorage key holding a code captured from a `?ref=` landing link. */
export const ACQ_REF_KEY = "acq_ref";

/**
 * Shape a `?ref=` value must have to be stored at capture time (the brief's
 * rule, wider than a stored code on purpose - the server decides validity).
 */
const REF_CAPTURE_RE = /^[A-Za-z0-9-]{3,24}$/;

/**
 * Twin of the backend's `attribution._CODE_SHAPE_RE` (5..24 chars, upper-case
 * letters, digits, inner hyphens). A value failing it can never match a stored
 * code, so the client skips the network round trip and the wrong-code budget.
 */
const CODE_SHAPE_RE = /^[A-Z0-9][A-Z0-9-]{3,22}[A-Z0-9]$/;

/** Longest code a sign-up body carries (`PARTNER_CODE_INPUT_MAX_LENGTH`). */
export const FRIEND_CODE_MAX_LENGTH = 24;

/** Shortest stored code (the floor of the shape rule below). */
export const FRIEND_CODE_MIN_LENGTH = 5;

/**
 * A `?ref=` value fit to store, upper-cased, or null.
 * @param {*} raw
 * @returns {string|null}
 */
export const captureRefValue = (raw) =>
  typeof raw === "string" && REF_CAPTURE_RE.test(raw) ? raw.toUpperCase() : null;

/**
 * The canonical form of a typed code: trimmed, inner whitespace removed,
 * upper-cased. Never validates - see `isFriendCodeShape`.
 * @param {*} value
 * @returns {string}
 */
export const normalizeFriendCode = (value) =>
  typeof value === "string" ? value.replace(/\s+/g, "").toUpperCase() : "";

/**
 * Could this (normalized) value be a stored code?
 * @param {string} code
 * @returns {boolean}
 */
export const isFriendCodeShape = (code) =>
  typeof code === "string" && CODE_SHAPE_RE.test(code);

/**
 * The link-captured code, or null. Never throws (private mode / blocked
 * storage simply means "no captured code").
 * @returns {string|null}
 */
export const readCapturedFriendCode = () => {
  try {
    return captureRefValue(sessionStorage.getItem(ACQ_REF_KEY));
  } catch (error) {
    return null;
  }
};

/**
 * The code a partner link brought this visit, or null: the captured
 * sessionStorage value first (first-writer-wins, as CaptureAcquisition
 * stores it), else the CURRENT URL's `?ref=`.
 *
 * The URL fallback is what makes a link straight to `/register?ref=CODE`
 * work: the sign-up page reads this during its FIRST render, and
 * CaptureAcquisition stores + strips `ref` in a passive EFFECT that runs after
 * that render - so on a direct landing the storage is still empty and the
 * value is only in the address bar. Same validation as the capture.
 *
 * @returns {string|null}
 */
export const readLandingFriendCode = () => {
  const captured = readCapturedFriendCode();
  if (captured) return captured;
  try {
    return captureRefValue(new URLSearchParams(window.location.search).get("ref"));
  } catch (error) {
    return null;
  }
};

/**
 * Forget the link-captured code. Called once an account has been CREATED in
 * this tab, so a second sign-up in the same session (log out, register
 * another account) does not inherit the first visitor's code as `link`.
 * Never throws.
 */
export const clearCapturedFriendCode = () => {
  try {
    sessionStorage.removeItem(ACQ_REF_KEY);
  } catch (error) {
    /* storage unavailable - nothing was stored either */
  }
};

/**
 * The two sign-up body fields for a code, or `{}` when there is nothing worth
 * sending. Only a value that passes the shape rule is sent - the server would
 * ignore anything else, and an omitted field is the "no code" contract.
 *
 * @param {string} code      as entered (normalized here)
 * @param {"link"|"typed"} source
 * @returns {{partner_code?: string, partner_code_source?: string}}
 */
export const signupFriendCodeFields = (code, source) => {
  const normalized = normalizeFriendCode(code);
  if (!isFriendCodeShape(normalized)) return {};
  return {
    partner_code: normalized,
    partner_code_source: source === "link" ? "link" : "typed",
  };
};

/**
 * Seconds from a 429's `Retry-After` header, clamped to 1..120, defaulting to
 * 5 when the header is missing or junk. Header lookup is case-insensitive
 * (axios lower-cases, fetch `Headers` does too, plain objects may not).
 *
 * @param {*} headers  a fetch `Headers`, or an axios headers object
 * @returns {number}
 */
export const retryAfterSeconds = (headers) => {
  let raw = null;
  try {
    if (headers && typeof headers.get === "function") {
      raw = headers.get("retry-after");
    } else if (headers && typeof headers === "object") {
      const key = Object.keys(headers).find((k) => k.toLowerCase() === "retry-after");
      raw = key ? headers[key] : null;
    }
  } catch (error) {
    raw = null;
  }
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return 5;
  return Math.min(seconds, 120);
};
